import { useMemo, useState } from "react";
import type { ActivityLog, ActivityType, FieldValue, Membership } from "@lifelog/shared";
import { coerceValues, daysAgoStr, todayStr } from "@lifelog/shared";
import { api, ApiError } from "../lib/api.js";
import { ModalShell } from "./ModalShell.js";
import { TimeSelector, dateTimeToOffsetIso } from "./TimeSelector.js";
import { ActivityFieldInputs, PerformedBySelect } from "./QuickLogModal.js";

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function addMonths(yearMonth: string, delta: number): string {
  const [y, m] = yearMonth.split("-").map(Number);
  const dt = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1 + delta, 1));
  return dt.toISOString().slice(0, 7);
}

function localTimeStr(d = new Date()): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function MonthGrid({
  selected,
  minDate,
  maxDate,
  onToggle,
}: {
  selected: Set<string>;
  minDate: string;
  maxDate: string;
  onToggle: (date: string) => void;
}) {
  const [yearMonth, setYearMonth] = useState(() => maxDate.slice(0, 7));
  const [year, month] = yearMonth.split("-").map(Number) as [number, number];
  const firstDow = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells: (number | null)[] = [
    ...Array.from({ length: firstDow }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const label = new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="select-none">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setYearMonth(addMonths(yearMonth, -1))}
          className="flex h-11 w-11 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{label}</span>
        <button
          type="button"
          onClick={() => setYearMonth(addMonths(yearMonth, 1))}
          disabled={yearMonth >= maxDate.slice(0, 7)}
          className="flex h-11 w-11 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800"
          aria-label="Next month"
        >
          ›
        </button>
      </div>
      <div className="mb-1 grid grid-cols-7 gap-px text-center">
        {DOW.map((d) => (
          <div key={d} className="text-[10px] font-medium text-gray-400">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px">
        {cells.map((day, idx) => {
          if (day === null) return <div key={`pad-${idx}`} />;
          const dateStr = `${yearMonth}-${String(day).padStart(2, "0")}`;
          const outOfRange = dateStr < minDate || dateStr > maxDate;
          const on = selected.has(dateStr);
          return (
            <button
              key={dateStr}
              type="button"
              disabled={outOfRange}
              onClick={() => onToggle(dateStr)}
              className={
                "flex min-h-11 items-center justify-center rounded-md text-xs " +
                (outOfRange
                  ? "opacity-30"
                  : on
                    ? "bg-(--accent) font-semibold text-(--accent-contrast)"
                    : "hover:bg-gray-100 dark:hover:bg-gray-800")
              }
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function BackfillModal({
  types,
  members,
  currentUserId,
  defaultPerformedBySelf,
  onClose,
  onSaved,
}: {
  types: ActivityType[];
  members: Membership[];
  currentUserId: string;
  defaultPerformedBySelf: boolean;
  onClose: () => void;
  onSaved: (logs: ActivityLog[]) => void;
}) {
  const choices = useMemo(
    () => types.filter((t) => !t.archived && !t.builtIn),
    [types],
  );
  const [mode, setMode] = useState<"single" | "multi">("single");
  const [typeId, setTypeId] = useState(choices[0]?.activityTypeId ?? "");
  const type = choices.find((t) => t.activityTypeId === typeId) ?? choices[0];
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [occurredAt, setOccurredAt] = useState<string | null>(null);
  const [dates, setDates] = useState<Set<string>>(new Set());
  const [timeStr, setTimeStr] = useState(localTimeStr);
  const [performedByUserId, setPerformedByUserId] = useState<string | undefined>(
    defaultPerformedBySelf ? currentUserId : undefined,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const minDate = daysAgoStr(365);
  const maxDate = todayStr();

  function setField(fieldId: string, value: FieldValue | undefined) {
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[fieldId];
      else next[fieldId] = value;
      return next;
    });
  }

  function pickType(id: string) {
    setTypeId(id);
    setValues({});
    setErrors({});
  }

  function toggleDate(date: string) {
    setDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  }

  async function save() {
    if (!type) {
      setError("Pick an activity.");
      return;
    }
    const coerced = coerceValues(type.fields, values);
    if (!coerced.ok) {
      setErrors(coerced.errors);
      return;
    }

    const stamps =
      mode === "single"
        ? [occurredAt]
        : [...dates]
            .sort()
            .map((d) => dateTimeToOffsetIso(d, timeStr));

    if (mode === "multi" && stamps.length === 0) {
      setError("Tap at least one date.");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const logs: ActivityLog[] = [];
      for (const stamp of stamps) {
        const log = await api<ActivityLog>("/logs", {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          json: {
            activityTypeId: type.activityTypeId,
            occurredAt: stamp ?? undefined,
            values: coerced.values,
            note: note.trim() || undefined,
            performedByUserId,
            source: "backfill",
          },
        });
        logs.push(log);
      }
      onSaved(logs);
    } catch (err) {
      if (err instanceof ApiError && err.fieldErrors) setErrors(err.fieldErrors);
      else setError(err instanceof Error ? err.message : "Couldn't save.");
    } finally {
      setBusy(false);
    }
  }

  const pillOn = "rounded-full px-4 py-1 bg-white text-(--accent) shadow-sm dark:bg-(--accent) dark:text-(--accent-contrast)";
  const pillOff = "rounded-full px-4 py-1 text-(--accent)";

  return (
    <ModalShell
      title="I did this a while ago"
      size="lg"
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="min-h-11 flex-1 rounded-lg border border-gray-300 text-sm text-gray-700 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy || !type}
            className="min-h-11 flex-1 rounded-lg bg-(--accent) text-sm font-medium text-(--accent-contrast) disabled:opacity-50"
          >
            {busy
              ? "Saving…"
              : mode === "multi"
                ? `Log ${dates.size || 0} date${dates.size === 1 ? "" : "s"}`
                : "Save"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </p>
        )}

        <div className="flex rounded-full bg-(--accent)/15 p-0.5 text-xs font-medium" role="group">
          <button type="button" onClick={() => setMode("single")} className={mode === "single" ? pillOn : pillOff}>
            Single
          </button>
          <button type="button" onClick={() => setMode("multi")} className={mode === "multi" ? pillOn : pillOff}>
            Multi-date
          </button>
        </div>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Activity</span>
          <select value={type?.activityTypeId ?? ""} onChange={(e) => pickType(e.target.value)} className={inputCls}>
            {choices.length === 0 && <option value="">No activities yet</option>}
            {choices.map((t) => (
              <option key={t.activityTypeId} value={t.activityTypeId}>
                {t.name}
              </option>
            ))}
          </select>
        </label>

        {type && (
          <ActivityFieldInputs
            key={type.activityTypeId}
            fields={type.fields}
            values={values}
            errors={errors}
            onChange={setField}
          />
        )}

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Note (optional)</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={`${inputCls} resize-none`} />
        </label>

        {mode === "single" ? (
          <div>
            <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">When</span>
            <TimeSelector value={occurredAt} onChange={setOccurredAt} maxDaysBack={365} />
          </div>
        ) : (
          <div className="space-y-3">
            <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Tap dates, then pick a time for all of them
            </span>
            <MonthGrid selected={dates} minDate={minDate} maxDate={maxDate} onToggle={toggleDate} />
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Time</span>
              <input
                type="time"
                value={timeStr}
                onChange={(e) => setTimeStr(e.target.value || "00:00")}
                className={inputCls}
              />
            </label>
          </div>
        )}

        <PerformedBySelect
          members={members}
          currentUserId={currentUserId}
          value={performedByUserId}
          onChange={setPerformedByUserId}
        />
      </div>
    </ModalShell>
  );
}
